import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { Panel } from '@/components/Panel';
import { HeritageSeal } from '@/components/BrandMark';
import { useCart } from '@/contexts/CartContext';
import { ArrowLeft, Loader2, Check, X, Truck, Tag } from 'lucide-react';

const NZ_REGIONS = [
  'Auckland', 'Bay of Plenty', 'Canterbury', 'Gisborne', 'Hawke\u2019s Bay', 'Manawat\u016B-Whanganui',
  'Marlborough', 'Nelson', 'Northland', 'Otago', 'Southland', 'Taranaki', 'Tasman',
  'Waikato', 'Wellington', 'West Coast',
];

function TextInput({ label, id, required, ...rest }) {
  return (
    <label htmlFor={id} className="block">
      <div className="h-eyebrow mb-1.5">{label}{required && <span className="ml-0.5" style={{ color: '#8A4E3A' }}>*</span>}</div>
      <input
        id={id}
        name={id}
        required={required}
        className="w-full rounded-md border px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2"
        style={{ borderColor: 'rgba(75,24,56,0.25)', color: 'var(--heritage-plum)' }}
        {...rest}
      />
    </label>
  );
}

export default function CheckoutPage() {
  const { lines, totals, clearCart } = useCart();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: '', email: '', phone: '',
    line1: '', line2: '', suburb: '', city: '', region: 'Auckland', postcode: '',
    country: 'New Zealand', notes: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Shipping
  const [shippingMethods, setShippingMethods] = useState([]);
  const [shippingMethod, setShippingMethod] = useState('');

  // Discount code
  const [codeInput, setCodeInput] = useState('');
  const [appliedDiscount, setAppliedDiscount] = useState(null); // {code, discount_amount, free_shipping, summary}
  const [codeError, setCodeError] = useState('');
  const [codeChecking, setCodeChecking] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/shop/shipping-methods');
        setShippingMethods(data.methods || []);
        if (data.methods?.length && !shippingMethod) setShippingMethod(data.methods[0].code);
      } catch { /* silent */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-apply the best qualifying discount when no manual code is entered.
  useEffect(() => {
    if (appliedDiscount || codeInput.trim()) return;
    if (lines.length === 0) return;
    let cancelled = false;
    (async () => {
      try {
        const payload = {
          subtotal: totals.subtotal,
          line_items: lines.map((l) => ({
            slug: l.slug, sku: l.sku, qty: l.qty, unit_price: l.unit_price,
            line_total: l.unit_price * l.qty,
          })),
        };
        // If shopper arrived via /ambassador/:code, apply that code first
        let refCode = '';
        try { refCode = localStorage.getItem('h_ref_code') || ''; } catch { refCode = ''; }
        if (refCode) {
          try {
            const { data } = await api.post('/shop/validate-discount', { code: refCode, ...payload });
            if (!cancelled) { setAppliedDiscount({ ...data, auto: true }); return; }
          } catch { /* fall through to auto-apply codes */ }
        }
        const { data } = await api.post('/shop/auto-discounts', payload);
        if (!cancelled && data?.best) setAppliedDiscount({ ...data.best, auto: true });
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lines, totals.subtotal]);

  const chosenMethod = shippingMethods.find((m) => m.code === shippingMethod);
  const rawShipping = chosenMethod ? Number(chosenMethod.rate || 0) : 0;
  const isFreeShipping =
    (appliedDiscount?.free_shipping) ||
    (chosenMethod?.free_over != null && totals.subtotal >= Number(chosenMethod.free_over));
  const shippingCost = isFreeShipping ? 0 : rawShipping;
  const discountAmount = Number(appliedDiscount?.discount_amount || 0);
  const orderTotal = Math.max(0, totals.subtotal - discountAmount) + shippingCost;

  const applyDiscount = async () => {
    setCodeError('');
    if (!codeInput.trim()) return;
    setCodeChecking(true);
    try {
      const payload = {
        code: codeInput.trim().toUpperCase(),
        subtotal: totals.subtotal,
        line_items: lines.map((l) => ({
          slug: l.slug, sku: l.sku, qty: l.qty, unit_price: l.unit_price,
          line_total: l.unit_price * l.qty,
        })),
      };
      const { data } = await api.post('/shop/validate-discount', payload);
      setAppliedDiscount(data);
    } catch (e) {
      setCodeError(e?.response?.data?.detail || 'Code not valid');
      setAppliedDiscount(null);
    } finally {
      setCodeChecking(false);
    }
  };

  const clearDiscount = () => { setAppliedDiscount(null); setCodeInput(''); setCodeError(''); };

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (lines.length === 0) { setError('Your bag is empty.'); return; }
    if (!shippingMethod) { setError('Please choose a shipping option.'); return; }
    setSubmitting(true);
    try {
      const payload = {
        items: lines.map((l) => ({ slug: l.slug, sku: l.sku, qty: l.qty })),
        shipping: {
          name: form.name, email: form.email, phone: form.phone,
          line1: form.line1, line2: form.line2, suburb: form.suburb,
          city: form.city, region: form.region, postcode: form.postcode,
          country: form.country, notes: form.notes,
        },
        origin_url: window.location.origin,
        discount_code: appliedDiscount?.code || '',
        shipping_method: shippingMethod,
      };
      const { data } = await api.post('/shop/checkout', payload);
      clearCart();
      if (data.url) {
        window.location.href = data.url;
      } else {
        navigate(`/payment/success?session_id=${encodeURIComponent(data.session_id)}`);
      }
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not start checkout. Please review your bag and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (lines.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-16 text-center">
        <HeritageSeal size={64} />
        <div className="h-eyebrow mt-4">Checkout</div>
        <div className="h-display text-3xl mt-2" style={{ color: 'var(--heritage-plum)' }}>Your bag is empty</div>
        <p className="opacity-80 mt-3 text-sm" style={{ color: 'var(--heritage-plum)' }}>
          Add a Heritage piece to your bag before checking out.
        </p>
        <div className="mt-6">
          <Link to="/store" className="h-btn"><ArrowLeft size={14} className="mr-1.5" /> Browse the store</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-6 py-10">
      <div className="mb-6">
        <Link to="/store" className="h-eyebrow inline-flex items-center gap-1.5 opacity-80 hover:opacity-100" data-testid="checkout-back">
          <ArrowLeft size={12} /> Continue shopping
        </Link>
        <div className="flex items-center gap-3 mt-4">
          <HeritageSeal size={44} />
          <div>
            <div className="h-eyebrow">Heritage Apparel</div>
            <h1 className="h-display text-3xl md:text-4xl" style={{ color: 'var(--heritage-plum)' }}>Checkout</h1>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="grid lg:grid-cols-3 gap-6" data-testid="checkout-form">
        {/* Left: form */}
        <div className="lg:col-span-2 space-y-6">
          <Panel className="p-6">
            <div className="h-serif text-xl mb-4" style={{ color: 'var(--heritage-plum)' }}>Contact</div>
            <div className="grid md:grid-cols-2 gap-4">
              <TextInput label="Full name" id="name" required value={form.name} onChange={update('name')} placeholder="Jane Smith" data-testid="checkout-name" />
              <TextInput label="Email" id="email" type="email" required value={form.email} onChange={update('email')} placeholder="jane@example.co.nz" data-testid="checkout-email" />
              <TextInput label="Phone" id="phone" type="tel" value={form.phone} onChange={update('phone')} placeholder="021 000 0000" data-testid="checkout-phone" />
            </div>
          </Panel>

          <Panel className="p-6">
            <div className="h-serif text-xl mb-4" style={{ color: 'var(--heritage-plum)' }}>Shipping address</div>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <TextInput label="Address line 1" id="line1" required value={form.line1} onChange={update('line1')} placeholder="12 Dahlia Lane" data-testid="checkout-line1" />
              </div>
              <div className="md:col-span-2">
                <TextInput label="Address line 2" id="line2" value={form.line2} onChange={update('line2')} placeholder="Apt / suite (optional)" data-testid="checkout-line2" />
              </div>
              <TextInput label="Suburb" id="suburb" value={form.suburb} onChange={update('suburb')} data-testid="checkout-suburb" />
              <TextInput label="City / town" id="city" required value={form.city} onChange={update('city')} placeholder="Auckland" data-testid="checkout-city" />
              <label htmlFor="region" className="block">
                <div className="h-eyebrow mb-1.5">Region</div>
                <select
                  id="region"
                  value={form.region}
                  onChange={update('region')}
                  className="w-full rounded-md border px-3 py-2.5 text-sm bg-white"
                  style={{ borderColor: 'rgba(75,24,56,0.25)', color: 'var(--heritage-plum)' }}
                  data-testid="checkout-region"
                >
                  {NZ_REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </label>
              <TextInput label="Postcode" id="postcode" required value={form.postcode} onChange={update('postcode')} placeholder="1010" data-testid="checkout-postcode" />
              <TextInput label="Country" id="country" value={form.country} onChange={update('country')} disabled data-testid="checkout-country" />
              <div className="md:col-span-2">
                <label htmlFor="notes" className="block">
                  <div className="h-eyebrow mb-1.5">Delivery notes</div>
                  <textarea
                    id="notes"
                    rows={2}
                    value={form.notes}
                    onChange={update('notes')}
                    className="w-full rounded-md border px-3 py-2.5 text-sm bg-white"
                    style={{ borderColor: 'rgba(75,24,56,0.25)', color: 'var(--heritage-plum)' }}
                    placeholder="Anything the courier should know"
                    data-testid="checkout-notes"
                  />
                </label>
              </div>
            </div>
          </Panel>

          {error && (
            <Panel className="p-4 border-l-4" style={{ borderLeftColor: '#8A4E3A' }}>
              <div className="text-sm" style={{ color: '#8A4E3A' }} data-testid="checkout-error">{error}</div>
            </Panel>
          )}
        </div>

        {/* Right: summary */}
        <div>
          <Panel className="p-6 sticky top-24 space-y-4" data-testid="checkout-summary">
            <div className="h-serif text-xl" style={{ color: 'var(--heritage-plum)' }}>Order summary</div>

            {/* Line items */}
            <div className="space-y-3 max-h-[240px] overflow-y-auto pr-1">
              {lines.map((l) => (
                <div key={l.sku} className="flex gap-3" data-testid={`summary-line-${l.sku}`}>
                  <img src={l.image_url} alt={l.name} className="w-14 h-16 object-cover rounded-md flex-shrink-0" style={{ background: '#FBF8F1' }} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm h-serif leading-tight" style={{ color: 'var(--heritage-plum)' }}>{l.name}</div>
                    <div className="text-[11px] opacity-75 mt-0.5" style={{ color: 'var(--heritage-plum)' }}>
                      {[l.size && `Size ${l.size}`, l.colour].filter(Boolean).join(' · ')}
                    </div>
                    <div className="text-xs mt-1" style={{ color: 'var(--heritage-plum)' }}>Qty {l.qty} · ${(l.unit_price * l.qty).toFixed(2)}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Shipping method selector */}
            {shippingMethods.length > 0 && (
              <div className="border-t pt-4" style={{ borderColor: 'rgba(75,24,56,0.15)' }}>
                <div className="h-eyebrow mb-2 flex items-center gap-1"><Truck size={11} /> Shipping</div>
                <div className="space-y-1.5" data-testid="checkout-shipping-methods">
                  {shippingMethods.map((m) => {
                    const isSel = shippingMethod === m.code;
                    const freeByThreshold = m.free_over != null && totals.subtotal >= Number(m.free_over);
                    return (
                      <label key={m.code}
                        className="flex items-center justify-between gap-2 rounded-md border p-2 cursor-pointer transition"
                        style={{ borderColor: isSel ? 'var(--heritage-plum)' : 'rgba(75,24,56,0.15)', background: isSel ? 'rgba(75,24,56,0.05)' : 'transparent' }}
                        data-testid={`shipping-option-${m.code}`}
                      >
                        <span className="flex items-center gap-2 min-w-0">
                          <input type="radio" name="shipping_method" value={m.code} checked={isSel}
                            onChange={() => setShippingMethod(m.code)} />
                          <span className="min-w-0">
                            <span className="text-sm h-serif" style={{ color: 'var(--heritage-plum)' }}>{m.name}</span>
                            {m.description && <span className="block text-[11px] opacity-75" style={{ color: 'var(--heritage-plum)' }}>{m.description}</span>}
                          </span>
                        </span>
                        <span className="text-sm h-serif whitespace-nowrap" style={{ color: 'var(--heritage-plum)' }}>
                          {freeByThreshold ? 'Free' : `$${Number(m.rate).toFixed(2)}`}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Discount code */}
            <div className="border-t pt-4" style={{ borderColor: 'rgba(75,24,56,0.15)' }}>
              <div className="h-eyebrow mb-2 flex items-center gap-1"><Tag size={11} /> Discount code</div>
              {appliedDiscount ? (
                <div className="flex items-center justify-between gap-2 rounded-md p-2" style={{ background: 'rgba(91,110,44,0.12)' }} data-testid="checkout-applied-discount">
                  <div className="min-w-0">
                    <div className="text-sm h-serif inline-flex items-center gap-1.5" style={{ color: 'var(--heritage-plum)' }}>
                      <Check size={12} /> {appliedDiscount.code}
                    </div>
                    <div className="text-[11px] opacity-80 mt-0.5" style={{ color: 'var(--heritage-plum)' }}>{appliedDiscount.summary}</div>
                  </div>
                  <button type="button" onClick={clearDiscount} className="opacity-70 hover:opacity-100" aria-label="Remove code" style={{ color: 'var(--heritage-plum)' }} data-testid="checkout-remove-discount">
                    <X size={14} />
                  </button>
                </div>
              ) : (
                <div>
                  <div className="flex gap-2">
                    <input
                      value={codeInput}
                      onChange={(e) => setCodeInput(e.target.value.toUpperCase().replace(/\s+/g, ''))}
                      placeholder="Enter code"
                      className="flex-1 rounded-md border px-3 py-2 text-sm bg-white"
                      style={{ borderColor: 'rgba(75,24,56,0.25)', color: 'var(--heritage-plum)' }}
                      data-testid="checkout-code-input"
                    />
                    <button type="button" onClick={applyDiscount} disabled={codeChecking || !codeInput.trim()}
                      className="h-btn-outline text-xs" data-testid="checkout-code-apply">
                      {codeChecking ? '...' : 'Apply'}
                    </button>
                  </div>
                  {codeError && <div className="text-[11px] mt-1.5" style={{ color: '#9A2C2C' }} data-testid="checkout-code-error">{codeError}</div>}
                </div>
              )}
            </div>

            {/* Totals */}
            <div className="space-y-1.5 text-sm border-t pt-4" style={{ borderColor: 'rgba(75,24,56,0.15)', color: 'var(--heritage-plum)' }}>
              <div className="flex justify-between"><span>Subtotal</span><span data-testid="summary-subtotal">${totals.subtotal.toFixed(2)}</span></div>
              {discountAmount > 0 && (
                <div className="flex justify-between" style={{ color: '#5B6E2C' }}>
                  <span>Discount ({appliedDiscount?.code})</span>
                  <span data-testid="summary-discount">− ${discountAmount.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span>Shipping {isFreeShipping && <span className="opacity-70">(free)</span>}</span>
                <span data-testid="summary-shipping">${shippingCost.toFixed(2)}</span>
              </div>
              <div className="h-divider my-2" />
              <div className="flex justify-between h-serif text-xl">
                <span>Total</span>
                <span data-testid="checkout-total">${orderTotal.toFixed(2)} NZD</span>
              </div>
            </div>

            <button
              type="submit"
              className="h-btn w-full inline-flex items-center justify-center gap-2"
              disabled={submitting}
              data-testid="checkout-pay"
            >
              {submitting ? <><Loader2 size={14} className="animate-spin" /> Preparing checkout...</> : 'Pay with card'}
            </button>
            <p className="text-[11px] opacity-70 text-center" style={{ color: 'var(--heritage-plum)' }}>
              You will be securely redirected to Stripe to complete payment. Your Heritage order will be reserved and confirmed on successful payment.
            </p>
          </Panel>
        </div>
      </form>
    </div>
  );
}
